import { useState } from "react";
import styles from "./ChangeLog.module.css";

export interface ChangeLogEntry {
  date: string;
  title: string;
  body: string;
}

export const CHANGELOG_ENTRIES: ChangeLogEntry[] = [
  {
    date: "2026-07-26",
    title: "Swipe Navigation & Bigger Word List",
    body: `You can now swipe left or right on the puzzle to move to the next or previous day — the same as the ‹ › arrows.

We roughly tripled the puzzle vocabulary — from about 5,000 words to nearly 16,000. Future puzzles now draw from a much richer word pool, so expect more variety day to day. Puzzles you've already played stay exactly as they were. We also tidied up the original list, removing abbreviations, foreign words, and other undesirable content we missed in the first cleanup.

Fixed the streak celebration on iPhone: the counter now flies back onto your streak badge cleanly instead of drifting off toward the corner.`,
  },
  {
    date: "2026-05-02",
    title: "Leaderboard Fixes",
    body: `Fixed two leaderboard bugs: your nickname is now preserved when you hide and re-show yourself (no more random resets), and your rank now always appears correctly in the win modal right after you solve.`,
  },
  {
    date: "2026-04-22",
    title: "Streak Popup Fix",
    body: `Fixed a display bug where the streak popup could show blank day boxes that were impossible given your streak length. Days within an active streak now correctly show as paused rather than empty.`,
  },
  {
    date: "2026-04-04",
    title: "Leaderboard: Wins View",
    body: `New leaderboard view! The "Wins" tab ranks players by their longest win streak, with current streak as a tiebreaker. Check it out and see who's been on the hottest run.

Also new: the Stats page now shows your average difficulty level.`,
  },
  {
    date: "2026-04-03",
    title: "Smarter Shuffling",
    body: `Puzzles now shuffle differently based on difficulty. Easier days scramble fewer letters, while harder days mix things up more aggressively. The minimum number of swaps to solve also scales with difficulty, so Saturday puzzles are genuinely trickier from the start.`,
  },
  {
    date: "2026-04-01",
    title: "Active Time Tracking",
    body: `The puzzle timer now tracks "active time" — only the time you're actually looking at the puzzle. Tab away to check something? That time doesn't count. You'll see both Active and Wall time on the win screen and in your Stats when they differ.

Thanks to Misty Kingfisher for the suggestion!`,
  },
  {
    date: "2026-03-31",
    title: "Retry Tracking & Leaderboard Fairness",
    body: `Retries are now properly tracked across the board. When you retry a puzzle, you'll see a red attempt badge (x2, x3, etc.) next to your swaps remaining. On the leaderboard, first-try solves always rank above retries, and retry wins no longer count toward your win rate.

Also fixed the streak animation on iPhones — it was occasionally sliding to the wrong spot before snapping into place.`,
  },
  {
    date: "2026-03-30",
    title: "Leaderboard",
    body: `See how you stack up! The new leaderboard lets you compare with other players across four views: fastest solve time, best star rating, win rate, and current streak. The default view rotates daily to keep things fresh.

Everyone gets a random nickname (like "Swift Falcon" or "Cosmic Penguin") when they sign in. You can hide yourself from the leaderboard anytime.`,
  },
  {
    date: "2026-03-28",
    title: "Tidied Up for Small Screens",
    body: `Hey Folks! The header was getting a bit cramped on phones, so we cleaned it up. On smaller screens, the action buttons now tuck into a neat little overflow menu and the date gets abbreviated. Everything stays centered and tidy now, even when your streak badge shows up.

Pro tip: Did you know you can always jump to the most recent incomplete puzzle by clicking on "GRIDDLE" in the header?`,
  },
  {
    date: "2026-03-27",
    title: "Streak Fixes",
    body: `Fixed a bug where streaks weren't computing correctly — your streak count should now match reality. Also tidied up how completion times are tracked behind the scenes.`,
  },
  {
    date: "2026-03-26",
    title: "Stats Overhaul & Difficulty Filters",
    body: `Big update to the Stats page! You can now filter by difficulty, sort by different columns, and see difficulty emoji next to each puzzle. Retries are flagged in red so you can see which ones broke your streak.

We also added a /latest link — jump straight to today's puzzle with a bookmarkable URL. That one was a feature request — thank you to everyone who has reported bugs and requested features!`,
  },
  {
    date: "2026-03-25",
    title: "Daily Difficulty System",
    body: `Puzzles now have a difficulty level that follows the day of the week — Sundays are chill, Saturdays are brutal. Each puzzle shows a difficulty badge with an emoji face so you know what you're getting into.`,
  },
  {
    date: "2026-03-23",
    title: "Streak Animations",
    body: `Your streak now gets a proper celebration! When you complete a puzzle, the streak count animates up from the badge with a little counting effect. We went through a few rounds of polish on this one — it should feel snappy and fun.`,
  },
  {
    date: "2026-03-22",
    title: "Streaks, Accounts & More",
    body: `The break was nice but now we've put together some cool new features for you!

Streaks are here! Play daily to build your streak — you get a two-day grace period if you miss a day. Your streak shows in the header and you can tap it for a detail view.

You can now sign in with Google to sync your progress across devices. We also added a privacy policy (boring but important) and fixed a white flash on page load in dark mode.

Restoring scrolling and pull-to-refresh was based on a user bug report — keep those reports coming!`,
  },
  {
    date: "2026-03-15",
    title: "Launch Week Fixes",
    body: `Lots of polish this week! The grid is now fully responsive on mobile — no more horizontal scrolling. The archive calendar highlights your solved and in-progress puzzles. We added a retry system so you can re-attempt puzzles, and fixed a progress data loss issue.

Also cleaned up the word list quite a bit — removed proper nouns, brands, and junk words that kept sneaking through.`,
  },
  {
    date: "2026-03-14",
    title: "Hello, World!",
    body: `Welcome to Griddle! 🎉 We launched today with daily 7×7 and 9×9 puzzles, an archive of past puzzles, stats tracking, a timer, and share-your-results. The word list is hand-curated and the puzzles are deterministic — same date always gives the same puzzle.

Have fun and let us know what you think!`,
  },
];

export const LATEST_CHANGELOG_DATE = CHANGELOG_ENTRIES[0].date;

const SEEN_KEY = "griddle-changelog-seen";

export function getSeenChangelogDate(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

export function markChangelogSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, LATEST_CHANGELOG_DATE);
  } catch { /* localStorage unavailable */ }
}

export function hasUnseenChangelog(): boolean {
  return getSeenChangelogDate() !== LATEST_CHANGELOG_DATE;
}

interface ChangeLogProps {
  onClose: () => void;
}

export function ChangeLog({ onClose }: ChangeLogProps) {
  const [selectedDate, setSelectedDate] = useState(CHANGELOG_ENTRIES[0].date);
  const entry = CHANGELOG_ENTRIES.find((e) => e.date === selectedDate) ?? CHANGELOG_ENTRIES[0];

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>What's New</h2>
          <select
            className={styles.datePicker}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          >
            {CHANGELOG_ENTRIES.map((e) => (
              <option key={e.date} value={e.date}>
                {new Date(e.date + "T00:00:00Z").toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  timeZone: "UTC",
                })}
              </option>
            ))}
          </select>
        </div>
        <h3 className={styles.entryTitle}>{entry.title}</h3>
        <div className={styles.body}>
          {entry.body.split("\n\n").map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
        <button className={styles.closeButton} onClick={onClose}>
          Got it!
        </button>
      </div>
    </div>
  );
}
